import React, { useState } from "react";
import { Select, Button } from "antd";
import { Download, Loader2 } from "lucide-react";
import { useQueryStates, parseAsInteger, parseAsString } from "nuqs";
import DataTable from "../../components/DataTable";
import { useEnquiryList, useDeleteEnquiry } from "../../../hooks/api/enquiry/hooks";
import { exportEnquiriesCsv } from "../../../hooks/api/enquiry/callbacks";
import { IEnquiry } from "../../../types/api/enquiry";
import { getEnquiryColumns } from "./EnquiryColumns";
import EnquiryDetailModal from "./EnquiryDetailModal";
import { useModalStore } from "../../../store/slices/modal";
import { MODAL_TYPES } from "../../../types/modal-types";
import { useMessageApi } from "../../../provider/AntdNotification";

const EnquiryList: React.FC = () => {
  const messageApi = useMessageApi();
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const blob = await exportEnquiriesCsv();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `artparks-enquiries-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      messageApi.error(err?.response?.data?.message || err?.message || "Export failed");
    } finally {
      setIsExporting(false);
    }
  };

  const [qData, setQData] = useQueryStates({
    page: parseAsInteger.withDefault(1),
    limit: parseAsInteger.withDefault(10),
    search: parseAsString.withDefault(""),
    type: parseAsString.withDefault(""),
    status: parseAsString.withDefault(""),
  });

  const queryParams = {
    page: qData.page,
    limit: qData.limit,
    search: qData.search || undefined,
    type: qData.type || undefined,
    status: qData.status || undefined,
    // Peter, Oct 2026: closed enquiries cluttered the main list - Active
    // (default) now excludes Closed server-side unless a specific status
    // (including Closed itself, via the filter below) is chosen.
    excludeClosed: !qData.status,
  };

  const { type: modalType, data: selectedEnquiryId, open, close } = useModalStore();

  const { data, isLoading, isError } = useEnquiryList(queryParams);

  const deleteMutation = useDeleteEnquiry({
    onSuccess: () => messageApi.success("Enquiry lead deleted successfully"),
    onError: (err: any) =>
      messageApi.error(
        err?.response?.data?.message || err?.message || "Failed to delete enquiry lead",
      ),
  });

  const handleView = (record: IEnquiry) => {
    open(MODAL_TYPES.VIEW_ENQUIRY_DETAILS, record._id);
  };

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id);
  };

  const columns = getEnquiryColumns({
    onView: handleView,
    onDelete: handleDelete,
  });

  return (
    <div className="p-6 min-h-screen">
      <DataTable
        title="Enquiry Leads"
        headerActions={
          <Button
            size="small"
            icon={isExporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            onClick={handleExport}
            disabled={isExporting}
          >
            Export CSV
          </Button>
        }
        dataSource={data?.data || []}
        columns={columns}
        loading={isLoading}
        isError={isError}
        rowKey={(record?: IEnquiry) => record?._id || String(Math.random())}
        searchProps={{
          searchValue: qData.search,
          onSearchChange: (v) => setQData({ search: v, page: 1 }),
          placeholder: "Search by name, email, phone, sculpture, or artist...",
          children: (
            <div className="flex gap-2">
              <Select
                placeholder="Type"
                value={qData.type || undefined}
                onChange={(val) => setQData({ type: val || "", page: 1 })}
                allowClear
                className="w-40"
                options={[
                  { value: "ENQUIRY", label: "Sculpture Enquiry" },
                  { value: "CONTACT_US", label: "Contact Us" },
                ]}
              />
              <Select
                placeholder="Status"
                value={qData.status || undefined}
                onChange={(val) => setQData({ status: val || "", page: 1 })}
                allowClear
                className="w-40"
                options={[
                  { value: "PENDING", label: "Pending" },
                  { value: "IN_PROGRESS", label: "In Progress" },
                  { value: "RESOLVED", label: "Resolved" },
                  { value: "CLOSED", label: "Closed" },
                ]}
              />
            </div>
          ),
        }}
        paginationProps={{
          page: qData.page,
          limit: qData.limit,
          total: data?.extra?.total || 0,
          onChange: (newPage, newLimit) =>
            setQData(
              newLimit !== qData.limit ? { page: 1, limit: newLimit } : { page: newPage },
            ),
        }}
      />

      <EnquiryDetailModal
        isOpen={modalType === MODAL_TYPES.VIEW_ENQUIRY_DETAILS}
        onClose={close}
        enquiryId={selectedEnquiryId}
      />
    </div>
  );
};

export default EnquiryList;
